/**
 * Theme Colors Constant
 * =====================================================================================
 *
 * This file defines all theme colors as TypeScript constants for use in components,
 * services, and runtime styling. It provides:
 *
 * 1. Semantic color definitions organized by theme (dark, light, whiteLabel, default)
 * 2. CSS variable name mappings for dynamic theme switching
 * 3. Helper functions for accessing theme colors programmatically
 *
 * USAGE:
 * ------
 * In TypeScript:
 *   import { THEME_COLORS, CSS_VAR_NAMES } from '@constants/theme-colors.constant';
 *
 *   const darkBgColor = THEME_COLORS.dark.backgrounds.primary;
 *   const cssVar = CSS_VAR_NAMES.backgrounds.primary; // '--theme-bg-primary'
 *
 * In components:
 *   element.style.backgroundColor = `var(${CSS_VAR_NAMES.backgrounds.primary})`;
 *
 * =====================================================================================
 */

import { Application } from "@enums/application.enums";
import { DarkPresetColorCodes, LightPresetColorCodes } from "@modules/edit-preference/components/whitelabel-site/whitelabel-site-constant";

/**
 * CSS Variable Names
 * Maps semantic names to CSS custom property names
 * Usage: `var(${CSS_VAR_NAMES.backgrounds.primary})`
 */
export const CSS_VAR_NAMES = {
  // Backgrounds
  backgrounds: {
    primary: '--theme-bg-primary',
    secondary: '--theme-bg-secondary',
    tertiary: '--theme-bg-tertiary',
    modal: '--theme-bg-modal',
    input: '--theme-bg-input',
    card: '--theme-bg-card',
    dark: '--theme-bg-dark',
    light: '--theme-bg-light',
    iconLight: '--theme-bg-icon-light',
    overlayDark: '--theme-bg-overlay-dark',
    overlayDarkAlt: '--theme-bg-overlay-dark-alt',
    footer: '--theme-bg-footer',
  },

  // Text & Typography
  text: {
    primary: '--theme-text-primary',
    secondary: '--theme-text-secondary',
    placeholder: '--theme-text-placeholder',
    dark: '--theme-text-dark',
    disabled: '--theme-text-disabled',
    inverse: '--theme-text-inverse',
    danger: '--theme-text-danger',
  },

  // Interactive States
  interactive: {
    link: '--theme-interactive-link',
    hover: '--theme-interactive-hover',
    focus: '--theme-interactive-focus',
    button: '--theme-interactive-button',
    accent: '--theme-interactive-accent',
  },

  // Borders & Dividers
  borders: {
    primary: '--theme-border-primary',
    secondary: '--theme-border-secondary',
    transparent: '--theme-border-transparent',
  },

  // Gradients
  gradients: {
    primary: '--theme-gradient-primary',
    secondary: '--theme-gradient-secondary',
    overlayDark: '--theme-gradient-overlay-dark',
    overlayLight: '--theme-gradient-overlay-light',
    sidebarOverlay: '--theme-gradient-sidebar-overlay',
    profileCard: '--theme-gradient-profile-card',
    buttonPrimary: '--theme-gradient-button-primary',
    buttonSecondary: '--theme-gradient-button-secondary',
    iconText: '--theme-gradient-icon-text',
  },

  // Special & Utility
  special: {
    searchBg: '--theme-search-bg',
    searchBgMobile: '--theme-search-bg-mobile',
    searchBgDesktop: '--theme-search-bg-desktop',
    messageStrip: '--theme-message-strip',
    shadowLight: '--theme-shadow-light',
  },
};

/**
 * Actual Color Values by Theme
 * These match the SCSS definitions in _theme-colors.scss
 */
export const THEME_COLORS = {
  dark: {
    backgrounds: {
      primary: DarkPresetColorCodes.bg_color,
      secondary: DarkPresetColorCodes.secondary_bg_color,
      tertiary: DarkPresetColorCodes.secondary_bg_color,
      modal: '#191a1c',
      input: '#424346',
      card: '#000000',
      dark: '#000',
      light: '#fff',
      iconLight: '#fff',
      overlayDark: 'rgba(0, 0, 0, 0.7)',
      overlayDarkAlt: 'rgba(51, 51, 51, 0.9)',
      footer: '#002339',
    },
    text: {
      primary: '#ffffff',
      secondary: '#9f9f9f',
      placeholder: '#9f9f9f',
      dark: '#131415',
      disabled: 'rgba(0, 0, 0, 0.25)',
      inverse: '#000',
      danger: '#d2006b',
    },
    interactive: {
      link: '#09c6da',
      hover: '#333',
      focus: 'rgba(0, 182, 234, 0.3)',
      button: '#424346',
      accent: '#00b6ea',
    },
    borders: {
      primary: 'rgba(255, 255, 255, 0.16)',
      secondary: '#474748',
      transparent: 'transparent',
    },
    gradients: {
      primary: 'linear-gradient(95deg, #1de9b6 0%, #00b6ea 100%)',
      secondary: 'linear-gradient(to bottom, #002740 50%, #002740 100%)',
      overlayDark: 'linear-gradient(to top, #131415, rgba(44, 44, 44, 0) 100%, rgba(0, 0, 0, 0) 0%)',
      overlayLight: 'linear-gradient(to top, #131415, rgba(155, 155, 155, 0) 33%, rgba(0, 0, 0, 0))',
      sidebarOverlay: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.5) 50%, rgba(0, 0, 0, 0.6))',
      profileCard: 'linear-gradient(to top, #131415, rgba(44, 44, 44, 0) 66%, rgba(0, 0, 0, 0) 34%)',
      buttonPrimary: 'linear-gradient(95deg, #1de9b6 0%, #00b6ea 100%)',
      buttonSecondary: 'linear-gradient(to bottom, #002740 50%, #002740 100%)',
      iconText: 'linear-gradient(95deg, #fff 0%, #fff 100%)',
    },
    special: {
      searchBg: '#424346',
      searchBgMobile: '#013f5e',
      searchBgDesktop: '#1a4158',
      messageStrip: '#424346',
      shadowLight: 'none',
    },
  },

  light: {
    backgrounds: {
      primary: LightPresetColorCodes.bg_color,
      secondary: LightPresetColorCodes.secondary_bg_color,
      tertiary: LightPresetColorCodes.secondary_bg_color,
      modal: '#fff',
      input: '#ecf2f5',
      card: '#fff',
      dark: '#292929',
      light: '#fff',
      iconLight: '#292929',
      overlayDark: 'rgba(0, 0, 0, 0.7)',
      overlayDarkAlt: 'rgba(51, 51, 51, 0.9)',
      footer: '#f7f8fa',
    },
    text: {
      primary: '#000',
      secondary: '#292929',
      placeholder: '#474748',
      dark: '#292929',
      disabled: 'rgba(0, 0, 0, 0.25)',
      inverse: '#fff',
      danger: '#d2006b',
    },
    interactive: {
      link: '#09c6da',
      hover: '#fff',
      focus: 'rgba(0, 182, 234, 0.2)',
      button: '#fff',
      accent: '#00b6ea',
    },
    borders: {
      primary: 'rgba(0, 0, 0, 0.16)',
      secondary: 'rgba(193, 193, 193, 0.5)',
      transparent: 'transparent',
    },
    gradients: {
      primary: 'linear-gradient(95deg, #1de9b6 0%, #00b6ea 100%)',
      secondary: 'linear-gradient(to bottom, #ecf2f5 50%, #ecf2f5 100%)',
      overlayDark: 'linear-gradient(to top, #fff, rgba(155, 155, 155, 0) 100%, rgba(0, 0, 0, 0) 0%)',
      overlayLight: 'linear-gradient(to top, #fff, rgba(155, 155, 155, 0) 33%, rgba(0, 0, 0, 0))',
      sidebarOverlay: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.5) 50%, rgba(0, 0, 0, 0.6))',
      profileCard: 'linear-gradient(to top, #fff, rgba(155, 155, 155, 0) 33%, rgba(0, 0, 0, 0))',
      buttonPrimary: 'linear-gradient(95deg, #1de9b6 0%, #00b6ea 100%)',
      buttonSecondary: 'linear-gradient(to bottom, #ecf2f5 50%, #ecf2f5 100%)',
      iconText: 'linear-gradient(95deg, #000 0%, #000 100%)',
    },
    special: {
      searchBg: '#ecf2f5',
      searchBgMobile: '#ecf2f5',
      searchBgDesktop: '#ecf2f5',
      messageStrip: '#ecf2f5',
      shadowLight: '0 2px 10px 0 rgba(0, 0, 0, 0.1)',
    },
  },

  default: {
    backgrounds: {
      primary: Application.DEFAULT_BG_COLOR,
      secondary: Application.DEFAULT_SECONDARY_BG_COLOR,
      tertiary: Application.DEFAULT_SECONDARY_BG_COLOR,
      modal: Application.DEFAULT_SECONDARY_BG_COLOR,
      input: '#1a3d53',
      card: Application.DEFAULT_SECONDARY_BG_COLOR,
      dark: '#000',
      light: '#fff',
      iconLight: '#333',
      overlayDark: 'rgba(0, 0, 0, 0.5)',
      overlayDarkAlt: 'rgba(0, 0, 0, 0.7)',
      footer: '#002339',
    },
    text: {
      primary: '#ffffff',
      secondary: '#9f9f9f',
      placeholder: '#9f9f9f',
      dark: '#000',
      disabled: 'rgba(0, 0, 0, 0.25)',
      inverse: '#000',
      danger: '#d2006b',
    },
    interactive: {
      link: '#09c6da',
      hover: '#1a4962',
      focus: 'rgba(0, 182, 234, 0.3)',
      button: '#1a3d53',
      accent: '#00b6ea',
    },
    borders: {
      primary: 'rgba(0, 182, 234, 0.5)',
      secondary: '#164462',
      transparent: 'transparent',
    },
    gradients: {
      primary: 'linear-gradient(95deg, #1de9b6 0%, #00b6ea 100%)',
      secondary: 'linear-gradient(to bottom, #002740 50%, #002740 100%)',
      overlayDark: 'linear-gradient(to top, #002740, rgba(1, 63, 94, 0) 100%, rgba(0, 0, 0, 0) 0%)',
      overlayLight: 'linear-gradient(to top, #002740, rgba(1, 63, 94, 0) 33%, rgba(0, 0, 0, 0))',
      sidebarOverlay: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.5) 50%, rgba(0, 0, 0, 0.6))',
      profileCard: 'linear-gradient(to top, #002740, rgba(1, 63, 94, 0) 66%, rgba(0, 0, 0, 0) 34%)',
      buttonPrimary: 'linear-gradient(95deg, #1de9b6 0%, #00b6ea 100%)',
      buttonSecondary: 'linear-gradient(to bottom, #002740 50%, #002740 100%)',
      iconText: 'linear-gradient(95deg, #fff 0%, #fff 100%)',
    },
    special: {
      searchBg: '#1a4158',
      searchBgMobile: '#013f5e',
      searchBgDesktop: '#1a4158',
      messageStrip: 'rgba(0, 182, 234, 0.09)',
      shadowLight: 'none',
    },
  },
};

/**
 * Helper function to get a color value by theme
 *
 * @param theme - Theme name ('dark', 'light', 'whiteLabel', 'default')
 * @param category - Color category (e.g., 'backgrounds', 'text')
 * @param key - Specific color key (e.g., 'primary')
 * @returns The color value as a string (hex, rgba, gradient, etc.)
 *
 * @example
 * const bgColor = getThemeColor('dark', 'backgrounds', 'primary'); // '#272a2b'
 */
export function getThemeColor(
  theme: 'dark' | 'light' | 'whiteLabel' | 'default',
  category: keyof typeof THEME_COLORS.dark,
  key: string
): string | undefined {
  return (THEME_COLORS[theme][category] as any)?.[key];
}

/**
 * Helper function to get CSS variable name for a color
 *
 * @param category - Color category (e.g., 'backgrounds', 'text')
 * @param key - Specific color key (e.g., 'primary')
 * @returns The CSS variable name (e.g., '--theme-bg-primary')
 *
 * @example
 * const varName = getCSSVarName('backgrounds', 'primary'); // '--theme-bg-primary'
 * element.style.backgroundColor = `var(${varName})`;
 */
export function getCSSVarName(
  category: keyof typeof CSS_VAR_NAMES,
  key: string
): string | undefined {
  return (CSS_VAR_NAMES[category] as any)?.[key];
}

/**
 * Helper function to apply CSS variable to element
 *
 * @param element - DOM element to apply style to
 * @param category - Color category
 * @param key - Color key
 * @param property - CSS property to apply (default: 'color')
 *
 * @example
 * applyCSSVariable(myElement, 'backgrounds', 'primary', 'backgroundColor');
 * // Sets: element.style.backgroundColor = 'var(--theme-bg-primary)';
 */
export function applyCSSVariable(
  element: HTMLElement,
  category: keyof typeof CSS_VAR_NAMES,
  key: string,
  property: string = 'color'
): void {
  const varName = getCSSVarName(category, key);
  if (varName && element) {
    element.style[property as any] = `var(${varName})`;
  }
}

/**
 * Export all theme names for type safety
 */
export type ThemeName = 'dark' | 'light' | 'whiteLabel' | 'default';
export type ColorCategory = keyof typeof THEME_COLORS.dark;

export const AVAILABLE_THEMES: Record<ThemeName, ThemeName> = {
  default: 'default',
  dark: 'dark',
  light: 'light',
  whiteLabel: 'whiteLabel'
};
